import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useIdleTimer } from 'react-idle-timer'
import { Authenticator, useAuthenticator } from "@aws-amplify/ui-react";
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { Container, Image } from 'semantic-ui-react';

import AmplitudeService from "services/amplitude";
import { updateLastLogin, updateLastActive } from "services/auth";

import Navigation from "components/Navigation";
import AddVendorWizard from "components/AddVendorWizard";
import PageHeader from "components/shared/PageHeader";
import VendorDocumentUpload from "components/VendorDocumentUpload";
import PublicUpload from "components/PublicUpload";
import AnimatedGradientBackground from "components/shared/AnimatedGradientBackground";

import ORGANIZATION_TYPES, {getHighestOrgType} from "constants/organization_types"
import {
  ADMIN_ROUTES,
  AGENCY_ROUTES,
  CLIENT_ROUTES
} from "constants/routes";

import { useDispatch, useSelector } from "react-redux";
import {vendorActions} from "redux/vendorSlice";
import {clientActions} from "redux/clientSlice";
import { loggedInUserState, userActions } from "redux/userSlice";
import { userOrganizationState, organizationActions, organizationState } from "redux/organizationSlice";

import {getLogo} from "services/appResources";

const routesMap = {
  [ORGANIZATION_TYPES.SYSTEM]: ADMIN_ROUTES,
  [ORGANIZATION_TYPES.AGENCY]: AGENCY_ROUTES,
  [ORGANIZATION_TYPES.CLIENT]: CLIENT_ROUTES,
}

const Loader = props => <div>{props.content || "loading"}</div>

export default function App() {
  const { user, authStatus, signOut } = useAuthenticator((context) => [
    context.user,
    context.authStatus,
    context.signOut
  ]);
  
  const [addVendorWizard, setAddVendorWizard] = useState(false);
  const [logo, setLogo] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userGroups, setUserGroups] = useState([]);
  const [signedOut, setSignedOut] = useState(false);

  // Track if we've updated last login this session
  const lastLoginUpdated = useRef(false);
  // Track last active timestamp update
  const lastActiveUpdated = useRef(null);

  const dispatch = useDispatch()
  const { userOrganization, userOrgType } = useSelector(userOrganizationState);
  const { selectedClient } = useSelector(organizationState);
  const clientMode = selectedClient?.mode
  const { loggedInUser } = useSelector(loggedInUserState)

  const fetchUserGroups = async () => {
    try {
      const { tokens } = await fetchAuthSession();
      const groups = tokens.accessToken.payload['cognito:groups'] || [loggedInUser?.role];
      setUserGroups(groups);
      dispatch(userActions.setUserGroups(groups));
    } catch (error) {
      console.error('Error fetching user groups:', error);
    }
  };

  const handleUserSession = async () => {
    if (authStatus === 'authenticated') {
      try {
        const currentUser = await getCurrentUser();
        const email = currentUser.signInDetails?.loginId || user?.email;
        setUserEmail(email);
        await fetchUserGroups();
        dispatch(organizationActions.getOrganizationByUser(email));
        
        // Check if user is disabled after organization data is fetched
        if (loggedInUser?.status === 'DISABLED') {
          setSignedOut(true);
          await signOut();
          return;
        }
        
        setSignedOut(false);
      } catch (error) {
        console.error('Error handling user session:', error);
      }
    }
  };

  useEffect(() => {
    handleUserSession();
  }, [authStatus]);

  // Update last login timestamp once per session
  useEffect(() => {
    const updateLoginTimestamp = async () => {
      console.log('[App] Last Login Update Check:', {
        authStatus,
        hasLoggedInUser: !!loggedInUser,
        userId: loggedInUser?.id,
        userEmail: loggedInUser?.email,
        alreadyUpdated: lastLoginUpdated.current
      });

      if (authStatus === 'authenticated' &&
          loggedInUser?.id &&
          loggedInUser?.email &&
          !lastLoginUpdated.current) {
        console.log('[App] Calling updateLastLogin for:', loggedInUser.email);
        lastLoginUpdated.current = true;
        const result = await updateLastLogin(loggedInUser.id, loggedInUser.email);
        console.log('[App] updateLastLogin result:', result);
      }
    };

    updateLoginTimestamp();
  }, [authStatus, loggedInUser]);

  // Reset last login flag when user signs out
  useEffect(() => {
    if (signedOut) {
      lastLoginUpdated.current = false;
      lastActiveUpdated.current = null;
    }
  }, [signedOut]);

  // Update last active timestamp every 15 minutes while user is active
  useEffect(() => {
    const updateActiveTimestamp = async () => {
      if (authStatus === 'authenticated' && loggedInUser?.id && loggedInUser?.email) {
        const now = Date.now();
        const fifteenMinutes = 15 * 60 * 1000; // 15 minutes in milliseconds

        // Update if this is the first time or if 15 minutes have passed
        if (!lastActiveUpdated.current || (now - lastActiveUpdated.current) >= fifteenMinutes) {
          console.log('[App] Updating lastActiveAt for:', loggedInUser.email);
          lastActiveUpdated.current = now;
          await updateLastActive(loggedInUser.id, loggedInUser.email);
        }
      }
    };

    // Run immediately on mount if authenticated
    updateActiveTimestamp();

    // Set up interval to check every 5 minutes (but only update every 15)
    const intervalId = setInterval(updateActiveTimestamp, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [authStatus, loggedInUser]);

  useEffect(() => {
    if (process.env.REACT_APP_AMPLITUDE_API_KEY) {
      AmplitudeService.initialize(process.env.REACT_APP_AMPLITUDE_API_KEY, user);
    }
  }, []);

  useEffect(() => {
    if (signedOut) {
      setLogo("");
      setUserEmail("");
      setUserGroups([]);
      dispatch(userActions.resetState());
      dispatch(vendorActions.resetState());
      dispatch(clientActions.resetState());
      dispatch(organizationActions.resetState());
      if (window.location.pathname !== "/") {
        window.history.pushState({}, "", "/");
      }
    }
  }, [signedOut]);

  useEffect(() => {
    if (user?.attributes?.email) {
      AmplitudeService.setUserId(user?.attributes?.email);
    }
  }, [user, AmplitudeService.isInitialized]);

  useEffect(() => {
    if(ORGANIZATION_TYPES.SYSTEM === userOrgType) {
      dispatch(organizationActions.getAgencies())
    }
    if(ORGANIZATION_TYPES.AGENCY === userOrgType) {
      dispatch(organizationActions.getClients(userOrganization.id))
    }
  }, [userOrgType, userOrganization])

  useEffect(() => {
    if(userOrganization?.name && userOrgType !== ORGANIZATION_TYPES.SYSTEM) {
      document.title = "MANACERT - " + userOrganization.name
    }
    if(userOrganization?.logo) {
      getLogo(
        userOrganization.logo,
        (response) => setLogo(response)
      )
    }
  }, [userOrganization])

  useEffect(() => {
    if (authStatus === 'authenticated' && loggedInUser?.status === 'DISABLED') {
      console.log('User account has been disabled - signing out');
      setSignedOut(true);
      signOut();
    }
  }, [loggedInUser?.status, authStatus]);

  const onIdle = async () => {
    setSignedOut(true);
    await signOut();
  };

  useIdleTimer({
    onIdle,
    timeout: 1000 * 60 * 30,
  });

  const onAddVendor = () => setAddVendorWizard(true)

  const childProps = {
    loggedInUser,
    userOrganization,
    clientMode,
    user: {
      attributes: { email: userEmail },
      signInUserSession: {
        accessToken: { payload: { 'cognito:groups': userGroups } }
      }
    },
    logo,
    onAddVendor,
  };

  function renderChildRoutes(children, routeKey) {
    return Object.keys(children).map(childRoute => {
      const ChildComponent = children[childRoute].element
      const path = String(childRoute).replace(`${routeKey}/`, "")
      return (
        <Route path={path} element={<ChildComponent {...childProps}/>} key={path} />
      )
    })
  }

  function renderOrgRoutes(organization) {
    const orgType = getHighestOrgType(organization.type)
    const orgRoutes = routesMap[orgType]
    if(orgRoutes) {
      return (
        <Routes>
            <Route exact path="/vendor/upload/:organizationId/:vendorId/:certificateId/" element={<VendorDocumentUpload />} >
              <Route path="/vendor/upload/:organizationId/:vendorId/:certificateId/:viewMode" element={<VendorDocumentUpload />} />
            </Route>
          {
            Object.keys(orgRoutes).map(routeKey => {
              const config = orgRoutes[routeKey]
              const ChildComponent = config.element
                return (
                  <Route
                    key={"route-" + routeKey}
                    path={routeKey}
                    element={
                      <ChildComponent {...childProps}>
                        {
                          config.container && config.children && renderChildRoutes(config.children, routeKey)
                        }
                      </ChildComponent>
                    }
                  >
                    {
                      config.container && config.children && renderChildRoutes(config.children, routeKey)
                    }
                  </Route>
                )
            })
          }
        </Routes>
      )
    }
    return <Loader />
  }

  return (
    <>
        <BrowserRouter>
      {
          authStatus === 'authenticated' && <>
          <PageHeader {...childProps} />
          <AddVendorWizard open={addVendorWizard} onClose={() => setAddVendorWizard(false)} clientMode={clientMode} loggedInUser={loggedInUser} />
          <Container fluid>
            {
              userOrganization ?
              <Navigation
              orgType={getHighestOrgType(userOrganization.type)}
              onAddVendor={onAddVendor}
              clientMode={clientMode}
              >
                  {
                    renderOrgRoutes(userOrganization)
                  }
                </Navigation>
                :
                <Loader />
              }

          </Container>
              </>
      }
      {
        authStatus === 'unauthenticated' &&
          <Routes>
              <Route exact path="/vendor/upload/:organizationId/:vendorId/:certificateId/" element={<VendorDocumentUpload />} >
                <Route path="/vendor/upload/:organizationId/:vendorId/:certificateId/:viewMode" element={<VendorDocumentUpload />} />
              </Route>
              <Route path="/upload/:token" element={<PublicUpload />} />
              <Route
              path="/*"
                element={
                  <div className="custom-login-wrapper">
                    <AnimatedGradientBackground />
                    <div className="custom-login-container-outer">
                      <div className="login-logo-container">
                        <Image
                          src={window.location.origin + "/app-logo-h110.png"}
                          className="login-logo-centered"
                          alt="MANACERT Logo"
                        />
                      </div>
                      <Authenticator className="custom-login-container"
                        hideSignUp={true}
                        initialState="signIn"
                        services={{
                          async handleSignIn(formData) {
                            const { username, password } = formData;
                            try {
                              const { signIn } = await import('aws-amplify/auth');
                              return await signIn({ username, password });
                            } catch (error) {
                              // Generic error message for any authentication failure
                              throw new Error('Incorrect information, please try again.');
                            }
                          },
                        }}
                        components={{
                          SignIn: {
                            Header() {
                              return null;
                            },
                          },
                          ForgotPassword: {
                            Header() {
                              return (
                                <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                                  <h2 style={{
                                    fontSize: '36px',
                                    fontWeight: '700',
                                    color: '#0f172a',
                                    marginBottom: '12px',
                                    lineHeight: '1.2',
                                    letterSpacing: '-0.02em',
                                    background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text'
                                  }}>
                                    Reset Password
                                  </h2>
                                  <p style={{
                                    fontSize: '15px',
                                    fontWeight: '400',
                                    color: '#64748b',
                                    marginTop: '12px'
                                  }}>
                                    Enter your email to receive a verification code
                                  </p>
                                </div>
                              );
                            },
                          },
                          ConfirmResetPassword: {
                            Header() {
                              return (
                                <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                                  <h2 style={{
                                    fontSize: '36px',
                                    fontWeight: '700',
                                    color: '#0f172a',
                                    marginBottom: '12px',
                                    lineHeight: '1.2',
                                    letterSpacing: '-0.02em',
                                    background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text'
                                  }}>
                                    Set New Password
                                  </h2>
                                  <p style={{
                                    fontSize: '15px',
                                    fontWeight: '400',
                                    color: '#64748b',
                                    marginTop: '12px'
                                  }}>
                                    Enter the code sent to your email and your new password
                                  </p>
                                </div>
                              );
                            },
                          },
                        }}
                      />
                      <div style={{
                        position: 'absolute',
                        bottom: '20px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        textAlign: 'center',
                        width: '100%',
                        pointerEvents: 'none'
                      }}>
                        <a
                          href="https://www.manacert.com/privacy-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#64748b',
                            fontSize: '13px',
                            textDecoration: 'none',
                            pointerEvents: 'auto'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.color = '#475569';
                            e.target.style.textDecoration = 'underline';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.color = '#64748b';
                            e.target.style.textDecoration = 'none';
                          }}
                        >
                          MANACERT Privacy Policy
                        </a>
                      </div>
                    </div>
                  </div>
                }
            />
          </Routes>
      }
        </BrowserRouter>
    </>
  );
}

